// ============================================================
//  „In den Plan" — Knopf plus Dialog. Der Dialog legt fest, WELCHE Personen dazugehören
//  (Trace-Umfang) und ob Halter-Maßnahmen mit in den Plan sollen (Verbund). Alle Zahlen sind
//  live gerechnet: Zielzustand mit dem, was schon im Plan liegt (R4) — nichts wird addiert.
//  Schnittstelle: <PlanButton leverId=… landscapeId?=… /> — zeigt „in den Plan" / „im Plan".
// ============================================================
import { useEffect, useMemo, useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { useApp } from "../../store/AppStore";
import { planStore, usePlanItems, TRACE_SCOPE_LABEL, type TraceScope } from "../../store/plan";
import { hypotheticalItem, planResult } from "../../domain/plan";
import { leverBreakdown } from "../../domain/analysis";
import { formatFue1, formatNum } from "../../domain/rules";
import { landscapeById } from "../../data/landscapes";
import { InfoTip } from "./Erklaerung";
import { RoleName } from "./RoleName";
import { Badge } from "./ui";

const SCOPES: TraceScope[] = ["nie", "nie+genutzt", "alle"];
const SCOPE_HINT: Record<TraceScope, string> = {
  nie: "Nur Personen, bei denen kein Konto den Wert im Trace-Zeitraum genutzt hat — gefahrlos.",
  "nie+genutzt": "Zusätzlich Personen, die den Wert nachweislich genutzt haben — Prozess abstimmen.",
  alle: "Zusätzlich Personen ohne Trace-Nachweis (System nicht im Trace) — nicht bewertbar.",
};

export function PlanButton({ leverId, landscapeId }: { leverId: string; landscapeId?: string }) {
  const items = usePlanItems();
  const chosen = items.some((i) => i.leverId === leverId);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium"
        style={chosen ? { backgroundColor: "var(--color-amp-green)", color: "#fff" } : { borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" }}>
        {chosen ? <><Check size={14} /> im Plan</> : <><Plus size={14} /> in den Plan</>}
      </button>
      {open && <PlanDialog leverId={leverId} landscapeId={landscapeId} onClose={() => setOpen(false)} />}
    </>
  );
}

// ------------------------------------------------------------ Dialog
export function PlanDialog({ leverId, landscapeId, onClose }: { leverId: string; landscapeId?: string; onClose: () => void }) {
  const { ds, ratio, price, contracted } = useApp();
  const items = usePlanItems();
  const existing = items.find((i) => i.leverId === leverId);
  const lv = ds.levers.find((l) => l.id === leverId);
  const [scope, setScope] = useState<TraceScope>(existing?.scope ?? "nie");
  const [checked, setChecked] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Plan-Kontext: alle Positionen außer dieser (bei „Umfang ändern" wird sie ersetzt).
  const others = useMemo(() => items.filter((i) => i.leverId !== leverId), [items, leverId]);
  const base = useMemo(() => planResult(ds, others, ratio, price), [ds, others, ratio, price, contracted]);
  // Je Umfang: was bringt diese Position im aktuellen Plan-Kontext (marginal, nicht addiert)?
  const perScope = useMemo(() => {
    const out = {} as Record<TraceScope, { persons: number; delta: number; inScope: number }>;
    for (const s of SCOPES) {
      const r = planResult(ds, [...others, hypotheticalItem(leverId, s)], ratio, price);
      out[s] = { persons: r.persons - base.persons, delta: r.reduction - base.reduction, inScope: r.perItem[leverId]?.inScope ?? 0 };
    }
    return out;
  }, [ds, others, leverId, ratio, price, base]);
  // Halter (strikt, aus der Aufschlüsselung) — mit Maßnahme → wählbar, sonst nur Hinweis.
  const bd = useMemo(() => leverBreakdown(leverId, ds, ratio), [leverId, ds, ratio]);
  const holdersWithLever = useMemo(() => bd.heldBy.filter((h) => h.leverId && h.leverId !== leverId && !others.some((o) => o.leverId === h.leverId)), [bd, others, leverId]);
  const holdersAlreadyInPlan = useMemo(() => bd.heldBy.filter((h) => h.leverId && others.some((o) => o.leverId === h.leverId)), [bd, others]);
  const holdersWithout = useMemo(() => bd.heldBy.filter((h) => !h.leverId), [bd]);
  const withItem = useMemo(() => planResult(ds, [...others, hypotheticalItem(leverId, scope)], ratio, price), [ds, others, leverId, scope, ratio, price, contracted]);
  // Je Halter-Maßnahme: was fällt zusätzlich, wenn sie mit demselben Umfang dazukommt?
  const perHolder = useMemo(() => {
    const out = new Map<string, { persons: number; delta: number }>();
    for (const h of holdersWithLever) {
      const r = planResult(ds, [...others, hypotheticalItem(leverId, scope), hypotheticalItem(h.leverId!, scope)], ratio, price);
      out.set(h.leverId!, { persons: r.persons - withItem.persons, delta: r.reduction - withItem.reduction });
    }
    return out;
  }, [ds, others, leverId, scope, ratio, price, holdersWithLever, withItem]);
  const final = useMemo(() => {
    const add = [hypotheticalItem(leverId, scope), ...[...checked].map((id) => hypotheticalItem(id, scope))];
    return planResult(ds, [...others, ...add], ratio, price);
  }, [ds, others, leverId, scope, checked, ratio, price]);

  if (!lv) return null;
  const land = landscapeById(lv.landscapeId);
  const ctxLand = landscapeId ? landscapeById(landscapeId) : undefined;
  const toggle = (id: string) => setChecked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const commit = () => {
    if (existing) planStore.update(leverId, { scope }); else planStore.add(leverId, scope);
    for (const id of checked) if (!planStore.has(id)) planStore.add(id, scope);
    onClose();
  };
  const remove = () => { planStore.remove(leverId); onClose(); };
  const finalDelta = final.reduction - base.reduction;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4" style={{ backgroundColor: "rgba(0,0,0,0.45)" }} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Maßnahme in den Plan" className="bg-card my-8 w-full max-w-2xl rounded-xl shadow-card" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }} onClick={(e) => e.stopPropagation()}>
        {/* Kopf */}
        <div className="flex items-start justify-between gap-4 border-b border-soft px-5 py-4">
          <div className="min-w-0">
            <div className="text-faint text-xs uppercase tracking-wide">{existing ? "Position im Plan" : "In den Plan"}</div>
            <div className="mt-1 text-base"><RoleName id={lv.roleId} name={lv.roleName} /></div>
            <div className="text-dim mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
              <span>System <span className="font-mono text-strong">{lv.sid}</span></span>
              <span>Landschaft <span className="text-strong">{land?.name ?? lv.landscapeId}</span></span>
              <span>{lv.authObject} {lv.field} = {lv.value}</span>
            </div>
            <div className="text-normal mt-1.5 text-sm">{lv.action}</div>
            {ctxLand && <div className="text-faint mt-1 text-xs">Aufgerufen aus Landschaft {ctxLand.name} — der Plan rechnet systemübergreifend über alle Personen.</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Schließen" className="text-faint hover:text-strong shrink-0 rounded-md p-1"><X size={18} /></button>
        </div>

        <div className="space-y-5 px-5 py-4">
          {/* Welche Personen */}
          <section>
            <div className="text-strong flex items-center gap-1 text-sm font-semibold">Welche Personen
              <InfoTip>Der Umfang bestimmt, wessen Rolle entfernt wird. Trace je Person = strengster Befund über alle ihre Konten dieses Systems: „genutzt", sobald ein Konto den Wert genutzt hat. Δ FUE ist der Zielzustand mit den {formatNum(others.length)} Positionen, die schon im Plan liegen — nichts wird addiert (R4).</InfoTip>
            </div>
            <div className="mt-2 space-y-1.5">
              {SCOPES.map((s) => {
                const v = perScope[s];
                return (
                  <label key={s} className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2" style={{ borderWidth: 1, borderColor: scope === s ? "var(--color-brand-600)" : "var(--border-soft)", backgroundColor: scope === s ? "var(--surface-accent)" : "transparent" }}>
                    <input type="radio" name="plan-scope" checked={scope === s} onChange={() => setScope(s)} />
                    <span className="min-w-0 flex-1">
                      <span className="text-strong block text-sm font-medium">{TRACE_SCOPE_LABEL[s]}</span>
                      <span className="text-faint block text-xs">{SCOPE_HINT[s]}</span>
                    </span>
                    <span className="tnum text-dim shrink-0 text-right text-xs">
                      <span className="text-strong block text-sm font-semibold">{formatNum(v.inScope)} <span className="text-faint font-normal">im Umfang</span></span>
                      <span className="block">{formatNum(v.persons)} fallen · <span style={{ color: "var(--color-amp-green)" }}>−{formatFue1(v.delta)}</span></span>
                    </span>
                  </label>
                );
              })}
            </div>
          </section>

          {/* Nur zusammen wirksam */}
          {(holdersWithLever.length > 0 || holdersWithout.length > 0 || holdersAlreadyInPlan.length > 0) && (
            <section>
              <div className="text-strong flex items-center gap-1 text-sm font-semibold">Nur zusammen wirksam
                <InfoTip>Personen, die nach dem Entfernen dieser Rolle von einer anderen Rolle oben gehalten werden (R3). Hat der Halter eine eigene Maßnahme, kann sie mit demselben Umfang in den Plan. Die Zahl „fallen zusätzlich" ist der Zielzustand mit beiden Positionen gegen den Zielzustand mit nur dieser.</InfoTip>
              </div>
              <div className="mt-2 space-y-1.5">
                {holdersWithLever.map((h) => {
                  const v = perHolder.get(h.leverId!);
                  return (
                    <label key={h.leverId} className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2" style={{ borderWidth: 1, borderColor: checked.has(h.leverId!) ? "var(--color-brand-600)" : "var(--border-soft)" }}>
                      <input type="checkbox" checked={checked.has(h.leverId!)} onChange={() => toggle(h.leverId!)} />
                      <span className="min-w-0 flex-1">
                        <RoleName id={h.roleId} name={h.roleName} className="text-sm" />
                        <span className="text-faint block text-xs">System <span className="font-mono">{h.sid}</span> · hält {formatNum(h.persons)} Träger oben · mit in den Plan</span>
                      </span>
                      <span className="tnum text-dim shrink-0 text-right text-xs">
                        <span className="text-strong block text-sm font-semibold">+{formatNum(v?.persons ?? 0)} <span className="text-faint font-normal">fallen zusätzlich</span></span>
                        <span className="block" style={{ color: "var(--color-amp-green)" }}>−{formatFue1(v?.delta ?? 0)}</span>
                      </span>
                    </label>
                  );
                })}
                {holdersAlreadyInPlan.map((h) => (
                  <div key={`in-${h.leverId}`} className="text-dim flex items-center gap-3 rounded-lg px-3 py-2 text-xs" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
                    <Check size={14} style={{ color: "var(--color-amp-green)" }} />
                    <span className="min-w-0 flex-1"><RoleName id={h.roleId} name={h.roleName} className="text-sm" /> <span className="text-faint">· <span className="font-mono">{h.sid}</span> · liegt schon im Plan</span></span>
                  </div>
                ))}
                {holdersWithout.map((h) => (
                  <div key={`${h.sid}|${h.roleId}`} className="text-dim flex items-center gap-3 rounded-lg px-3 py-2 text-xs" style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}>
                    <span className="min-w-0 flex-1">
                      <RoleName id={h.roleId} name={h.roleName} className="text-sm" />
                      <span className="text-faint block">System <span className="font-mono">{h.sid}</span> · {h.roleId === "#DEV#" ? "Entwicklerschlüssel" : h.removable ? "entfernbar, aber ohne wirksame Maßnahme" : "nicht entfernbar"}</span>
                    </span>
                    <Badge tone="gray">{formatNum(h.persons)} bleiben oben</Badge>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Ergebnis */}
          <section className="rounded-lg px-4 py-3" style={{ backgroundColor: "var(--surface-sunken)" }}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-dim text-sm">Ergebnis im Plan</span>
              <span className="tnum text-lg font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatFue1(finalDelta)}</span>
            </div>
            <div className="text-faint mt-0.5 flex flex-wrap justify-between gap-x-4 text-xs">
              <span>{formatNum(final.persons - base.persons)} Personen fallen zusätzlich · {formatNum(1 + checked.size)} {checked.size ? "Positionen" : "Position"} · Umfang {TRACE_SCOPE_LABEL[scope]}</span>
              <span className="tnum">Plan gesamt danach −{formatFue1(final.reduction)} · {formatNum(final.items)} Positionen</span>
            </div>
          </section>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-soft px-5 py-3">
          {existing && <button type="button" onClick={remove} className="mr-auto rounded-lg px-3 py-1.5 text-sm font-medium" style={{ color: "var(--color-amp-red)" }}>Aus dem Plan entfernen</button>}
          <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium" style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" }}>Abbrechen</button>
          <button type="button" onClick={commit} className="rounded-lg px-3 py-1.5 text-sm font-medium" style={{ backgroundColor: "var(--color-brand-600)", color: "#fff" }}>{existing ? "Umfang ändern" : "In den Plan"}</button>
        </div>
      </div>
    </div>
  );
}
